/**
 * Password policy — single source of truth
 *
 * One pure, framework-free validator shared by the API (Effect/Bun) and the web
 * SPA (via `@chatsdet/ui`'s strength meter), so backend and frontend can never
 * disagree on what counts as an acceptable password.
 *
 * Rule (length-first heuristic — accepts high-entropy passphrases):
 *   valid = length >= 8
 *        && length <= 200
 *        && (length >= 16 || (hasLetter && hasDigit))
 *
 * A 16+ character passphrase (e.g. "correct horse battery staple") is accepted
 * without any letter/number requirement, while shorter passwords still need a
 * letter and a number.
 *
 * @module Password
 */

export const PASSWORD_POLICY = {
  /** Minimum length for any password. */
  minLength: 8,
  /** Upper bound — guards against argon2 hashing DoS on absurd input. */
  maxLength: 200,
  /** At/above this length, the letter+number requirement is waived (passphrase). */
  passphraseLength: 16,
} as const;

export type PasswordRequirementId = 'length' | 'complexity';

export interface PasswordRequirement {
  id: PasswordRequirementId;
  label: string;
  met: boolean;
}

export type PasswordStrengthLevel = 'weak' | 'fair' | 'good' | 'strong';

export interface PasswordValidationResult {
  /** Overall gate. Invariant: `valid === requirements.every((r) => r.met)`. */
  valid: boolean;
  /** Drives the checklist UI (gate-aligned with `valid`). */
  requirements: PasswordRequirement[];
  /** Human-readable messages for the UNMET requirements (backend joins with '. '). */
  reasons: string[];
  /** 0–100, for the strength meter. */
  score: number;
  /** Coarse bucket derived from `score`, for the meter's color/label. */
  level: PasswordStrengthLevel;
}

const COMPLEXITY_LABEL = 'Include a letter and a number — or use 16+ characters';

/**
 * Validate a password against {@link PASSWORD_POLICY} and compute meter data.
 *
 * Pure and dependency-free so it can run identically in the browser and on Bun.
 */
export function validatePassword(password: string): PasswordValidationResult {
  const length = password.length;
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const isPassphrase = length >= PASSWORD_POLICY.passphraseLength;

  const lengthOk = length >= PASSWORD_POLICY.minLength && length <= PASSWORD_POLICY.maxLength;
  const complexityOk = isPassphrase || (hasLetter && hasDigit);

  const requirements: PasswordRequirement[] = [
    {
      id: 'length',
      label: `At least ${PASSWORD_POLICY.minLength} characters`,
      met: lengthOk,
    },
    {
      id: 'complexity',
      label: COMPLEXITY_LABEL,
      met: complexityOk,
    },
  ];

  const reasons: string[] = [];
  if (!lengthOk) {
    reasons.push(
      length > PASSWORD_POLICY.maxLength
        ? `Password must be at most ${PASSWORD_POLICY.maxLength} characters`
        : `Password must be at least ${PASSWORD_POLICY.minLength} characters`
    );
  }
  if (!complexityOk) {
    reasons.push(
      `Add a letter and a number, or make it at least ${PASSWORD_POLICY.passphraseLength} characters long`
    );
  }

  const valid = requirements.every((r) => r.met);
  const { score, level } = scorePassword(password, isPassphrase);

  return { valid, requirements, reasons, score, level };
}

function scorePassword(
  password: string,
  isPassphrase: boolean
): { score: number; level: PasswordStrengthLevel } {
  const length = password.length;
  if (length === 0) {
    return { score: 0, level: 'weak' };
  }

  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSymbol = /[^a-zA-Z0-9]/.test(password);
  const classCount = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;

  let score = 0;
  // Length carries the most weight — a long passphrase should read as strong
  // even with a single character class.
  score += Math.min(50, Math.round((length / PASSWORD_POLICY.passphraseLength) * 50));
  // Character variety: reward each class beyond the first, up to 30.
  score += Math.max(0, classCount - 1) * 10;
  // Explicit passphrase bonus.
  if (isPassphrase) score += 20;
  score = Math.min(100, score);

  let level: PasswordStrengthLevel;
  if (score < 40) level = 'weak';
  else if (score < 60) level = 'fair';
  else if (score < 80) level = 'good';
  else level = 'strong';

  return { score, level };
}
